import { yupResolver } from '@hookform/resolvers/yup'
import {
  Button,
  CircularProgress,
  DialogActions,
  Table,
  TableContainer,
  TableRow,
  TableCell,
  TableBody,
  TableHead,
  styled,
  Typography,
  Checkbox
} from '@mui/material'
import { FC, useEffect } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'

// component import
import { Alert } from '../../../common/Alert'
import { CustomController } from '../../../common/CustomController'
import { CustomModal } from '../../../common/CustomModal'

// others
import { useWriteOffBillsMutation, WriteOffBillsInput } from '../../../../../generated'
import { CANCEL } from '../../../../constants'
import { WriteOffBillInput, WriteOffBillsFormValues } from '../../../../interfaceTypes/forms'
import { writeOffBillsValidationSchema } from '../../../../validationSchema'
import { formatCurrency } from '../../../../utils'
import useFetchBillDetails from '../../../../hooks/useFetchBillDetails'
import { calculateClientBillData } from '../../../../utils/billing'

interface WriteOffBillsModalProps {
  isOpen: boolean
  billId: number
  clientId: string
  onClose: () => void
  onSuccess: () => void
}

const CustomTable = styled(Table)(({ theme }) => ({
  border: '1px solid #E0E0E0',
  '& .MuiTableCell-root': {
    padding: theme.spacing(1.5),
    borderBottom: '1px solid #E0E0E0'
  },
  '& .MuiTableCell-head': {
    fontWeight: 600,
    backgroundColor: theme.palette.grey[50]
  }
}))

export const WriteOffBillsModal: FC<WriteOffBillsModalProps> = ({ isOpen, billId, clientId, onClose, onSuccess }) => {
  const { bill, outstandingBillsData, interestData } = useFetchBillDetails({
    billId,
    includeInterest: true,
    selectedClientId: clientId
  })

  const methods = useForm<WriteOffBillsFormValues>({
    mode: 'all',
    resolver: yupResolver(writeOffBillsValidationSchema),
    defaultValues: {
      bills: [],
      writtenOffReason: ''
    }
  })

  const { handleSubmit, watch, setValue } = methods

  const [writeOffBills, { loading: writingOff }] = useWriteOffBillsMutation({
    onCompleted: data => {
      if (data?.writeOffBills?.response?.status === 200) {
        Alert.success('Bill(s) written off successfully')
        handleClose()
        onSuccess()
      }
    },
    onError: error => {
      Alert.error(error.message || 'Failed to write off bills')
    }
  })

  const selectedAccessToken = bill?.accessTokens?.find(token => token.client?.id === clientId)
  const clientWrittenOff =
    (selectedAccessToken?.writtenOffPrincipal || 0) + (selectedAccessToken?.writtenOffInterest || 0)
  const billWrittenOff = (bill?.writtenOffPrincipal || 0) + (bill?.writtenOffInterest || 0)

  const { payableAmount: clientBalance } = calculateClientBillData({
    paymentAllocations: bill?.paymentAllocations || [],
    // 0 previous makes it return only current bill balance
    previousBalance: 0,
    bill: {
      amount: bill?.amount,
      splitBill: !!bill?.case?.billing?.splitBillEnabled,
      status: bill?.status,
      discount: bill?.discount,
      interestAccrued: interestData.interestAccrued,
      writtenOff: billWrittenOff
    },
    client: { id: clientId, share: selectedAccessToken?.amount, writtenOff: clientWrittenOff }
  })

  const bills = watch('bills')
  const totalWriteOffAmount = bills.reduce((sum, bill) => {
    if (!bill.isSelected) return sum

    return sum + Number(bill.writtenOffPrincipal) + Number(bill.writtenOffInterest)
  }, 0)

  const handleClose = () => {
    onClose()
  }

  const handleBillSelection = (index: number, checked: boolean) => {
    const updatedBills = [...bills]
    updatedBills[index].isSelected = checked

    if (checked) {
      bills[index].writtenOffPrincipal = bills[index].principalBalance
      bills[index].writtenOffInterest = bills[index].interestDue
    } else {
      bills[index].writtenOffPrincipal = 0
      bills[index].writtenOffInterest = 0
    }

    setValue('bills', bills)
  }

  const onSubmit: SubmitHandler<WriteOffBillsFormValues> = async data => {
    const { bills: billsInput, writtenOffReason } = data

    const billsToWriteOff: WriteOffBillsInput['bills'] = billsInput
      .filter(bill => bill.isSelected && (bill.writtenOffPrincipal !== 0 || bill.writtenOffInterest !== 0))
      .map(bill => ({
        billId: bill.billId,
        clientId,
        writtenOffInterest: bill.writtenOffInterest,
        writtenOffPrincipal: bill.writtenOffPrincipal
      }))

    if (billsToWriteOff.length === 0) {
      Alert.error('Please select at least one bill to write off')
      return
    }

    writeOffBills({
      variables: {
        writeOffBillsInput: {
          bills: billsToWriteOff,
          writtenOffReason
        }
      }
    })
  }

  useEffect(() => {
    if (!bill?.id) return

    const billsData: WriteOffBillInput[] = []

    if (outstandingBillsData?.outstandingBills?.length > 0) {
      billsData.push(
        ...outstandingBillsData.outstandingBills.map(bill => ({
          billId: bill.billId,
          principalBalance: bill.principalBalance,
          interestDue: bill.interestDue,
          totalDue: bill.totalDue,
          writtenOffPrincipal: bill.principalBalance,
          writtenOffInterest: bill.interestDue,
          isSelected: true
        }))
      )
    }

    const principalBalance = clientBalance - interestData.interestDue
    billsData.push({
      billId: bill.id,
      principalBalance,
      interestDue: interestData.interestDue,
      totalDue: clientBalance,
      writtenOffPrincipal: principalBalance,
      writtenOffInterest: interestData.interestDue,
      isSelected: true
    })

    setValue('bills', billsData)
  }, [
    bill?.amount,
    bill?.amountPaid,
    bill?.writtenOffPrincipal,
    bill?.id,
    interestData.interestDue,
    outstandingBillsData.outstandingBills,
    clientBalance,
    setValue
  ])

  return (
    <CustomModal title="Write Off Remaining Balance" isOpen={isOpen} handleClose={handleClose} maxWidth="lg">
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Typography>Review and adjust the amounts to write off for each bill.</Typography>

          <TableContainer sx={{ mt: 2, mb: 4 }}>
            <CustomTable>
              <TableHead>
                <TableRow>
                  <TableCell width="5%"></TableCell>
                  <TableCell width="10%">Invoice #</TableCell>
                  <TableCell width="15%">Invoice Balance</TableCell>
                  <TableCell width="15%">Interest Due</TableCell>
                  <TableCell width="15%">Total Outstanding</TableCell>
                  <TableCell width="15%">Write Off Invoice Balance</TableCell>
                  <TableCell width="15%">Write Off Interest</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {bills.map((bill, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Checkbox
                        checked={bill.isSelected}
                        onChange={e => handleBillSelection(index, e.target.checked)}
                        size="small"
                      />
                    </TableCell>

                    <TableCell>{bill.billId}</TableCell>
                    <TableCell>{formatCurrency(bill.principalBalance)}</TableCell>
                    <TableCell>{formatCurrency(bill.interestDue)}</TableCell>
                    <TableCell>{formatCurrency(bill.totalDue)}</TableCell>

                    <TableCell>
                      <CustomController
                        size="small"
                        controllerName={`bills.${index}.writtenOffPrincipal`}
                        controllerLabel=""
                        fieldType="number"
                        dollarAdornment
                        sx={{ my: 0 }}
                      />
                    </TableCell>

                    <TableCell>
                      <CustomController
                        size="small"
                        controllerName={`bills.${index}.writtenOffInterest`}
                        controllerLabel=""
                        fieldType="number"
                        dollarAdornment
                        sx={{ my: 0 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </CustomTable>
          </TableContainer>

          <CustomController
            controllerName="writtenOffReason"
            controllerLabel="Notes"
            fieldType="textarea"
            isMultiLine
            rowsLength={3}
            sx={{ my: 0 }}
          />

          <Typography variant="subtitle1" color="warning.main" sx={{ mt: 2 }}>
            Warning: This action cannot be undone. Please review all amounts carefully before proceeding.
          </Typography>

          <DialogActions sx={{ padding: '30px 0 0' }}>
            <Button onClick={handleClose} disabled={writingOff}>
              {CANCEL}
            </Button>

            <Button
              type="submit"
              variant="contained"
              disabled={writingOff || !bills?.length || !totalWriteOffAmount}
              endIcon={writingOff && <CircularProgress size={20} color="inherit" />}
            >
              Write Off {formatCurrency(totalWriteOffAmount)}
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </CustomModal>
  )
}
